import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './action-bar.module.scss';
import layoutStyles from '../Post/post.module.scss';
import ShareBar from '../ShareBar';
import CommentsContext from '../../contexts/comments';
import { useCommentsCtaButtonTracking } from '../../hooks/analytics/useCommentsCtaButtonTracking';
import { attachClientImpressionHandler } from '../../hooks/analytics/client-event-tracking';
import dynamicImport from '../../utils/dynamic-import';
import { isNews } from '../../utils/isNews';
import NativeShareButton from '../NativeShareButton';
import { isNativeShareEligible } from '../../utils/sharing';
import FloatingButtons from './FloatingButtons';
import { isGeneratorPage } from '../../utils/isGeneratorPage';
import { isGamesPost } from '../../utils/games';
import BookmarkButton from '../BookmarkButton';

const CommentsCtaButton = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "comments-cta-button" */ '@buzzfeed/react-components/dist/module/lib/components/CommentsCtaButton'
    ),
  { ssr: false }
);

export function ActionBar({
  buzz,
  commentCount,
  showShareBar,
  className = '',
  destination = 'buzzfeed',
  isFloatingButtonsEnd = false,
  showCommentButton = true,
  designUpdateEnabled = false,
  isQuiz = false,
}) {
  const commentsCtaButtonTrackingFn = useCommentsCtaButtonTracking(buzz);
  const {
    commentsEnabled,
    isCommentsPanelOpen,
    toggleCommentsPanel
  } = useContext(CommentsContext);
  const [isMobileShareEnabled, setIsMobileShareEnabled] = useState(false); // Is able to native share on mobile device

  const onLoaded = isNews(buzz) ?
    ({ element, index, platform, shareUrl }) => {
      attachClientImpressionHandler(
        element,
        buzz,
        {
          subunit_name: 'share',
          subunit_type: 'component',
          unit_type: 'buzz_head',
          unit_name: buzz.id,
          item_name: platform,
          item_type: 'button',
          position_in_subunit: index,
          target_content_type: 'url',
          target_content_id: shareUrl
        }
      );
    } : undefined;

  useEffect(() => {
    const storedComment = window.localStorage.getItem('COMMENT_' + window.location.host + window.location.pathname);
    const openCommentsParam = (new URLSearchParams(window.location.search)).has('open_comments');
    if (openCommentsParam && buzz && isGeneratorPage(buzz)) {
      // Do not open comments panel from a generator page,
      // try to scroll to the ai-generated content instead
      const aiGeneratedContent = document.querySelector('#generator-comments-section');
      if (aiGeneratedContent) {
        aiGeneratedContent.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    const openCommentsPanel = storedComment || openCommentsParam;
    if (commentCount !== null && openCommentsPanel) {
      toggleCommentsPanel(true);
    }
  }, [commentCount, buzz]);

  useEffect(() => {
    if (isNativeShareEligible()) {
      setIsMobileShareEnabled(true);
    }
  }, []);

  const commentsCtaButton = (commentsEnabled && showCommentButton && !isGeneratorPage(buzz) &&
    <CommentsCtaButton
      commentCount={commentCount}
      showCount={true}
      track={commentsCtaButtonTrackingFn}
      locale={buzz.language || 'en'}
      destination={destination}
      onCtaClick={() => toggleCommentsPanel(true)}
      disabled={isCommentsPanelOpen}
      variant={designUpdateEnabled ? (isQuiz ? 'fillWhite' : 'fillPurple') : ''}
    />
  );

  const shareBar = (showShareBar &&
    <ShareBar
      buzz={buzz}
      type="pageLevelOutlineThick"
      position="top_share_bar"
      className={layoutStyles.shareTop}
      onLoaded={onLoaded}
      designUpdateEnabled={designUpdateEnabled}
    />
  );

  const nativeShareButton = (isMobileShareEnabled &&
    <NativeShareButton
      variant={isQuiz ? 'quiz' : designUpdateEnabled ? (isGamesPost(buzz) ? 'games' : 'outlinePurpleThick') : 'standard'}
      includeShortTitle
      trackingData={{
        unit_name: buzz.id,
        unit_type: 'buzz_head',
      }}
    />
  );

  if (buzz.hasQuizSharedResult) {
    return null;
  }

  const actionBarClasses = [
    styles.actionBar,
    styles[destination],
    className,
    'embed-action-bar',
    designUpdateEnabled && styles.postHead,
    (buzz?.classification?.edition === 'Japan' && !commentsCtaButton) && styles.actionBarJP,
  ].filter(Boolean).join(' ');

  return (
    <div className={actionBarClasses}>
      <FloatingButtons isFloatingButtonsEnd={isFloatingButtonsEnd} buzz={buzz} commentCount={commentCount} />
      {!isNews(buzz) ? <>
        {/*
          Needs to use additional wrapper div to save right alignment for shareBar if there is no commentsCtaButton
          on the page
        */}
        <div className={styles.commentsCtaWrapper}>
          {commentsCtaButton}
          {isQuiz && <BookmarkButton buzz={buzz} />}
          {nativeShareButton}
        </div>
        {shareBar}
      </> : <>
        {shareBar}
        {commentsCtaButton}
      </>}
    </div>
  );
}

ActionBar.propTypes = {
  buzz: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  isFloatingButtonsEnd: PropTypes.bool,
  isQuiz: PropTypes.bool,
};

export default ActionBar;
